import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Edit, 
  Trash2, 
  GripVertical, 
  Video, 
  FileText, 
  Package, 
  Code, 
  Upload,
  Play,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  X,
  File,
  Youtube,
  Link as LinkIcon,
  ClipboardList,
  CheckCircle,
  Circle,
  HelpCircle,
  Sparkles,
  Eye,
  Send,
  GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import QuizBuilder from './QuizBuilder';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

// Helper to convert storage URLs to proxy URLs
const getProxyUrl = (url) => {
  if (!url) return null;
  // Check if it's an Emergent storage URL
  if (url.includes('integrations.emergentagent.com/objstore')) {
    const match = url.match(/(navigate-lms|cerebro-lms)\/(.+)$/);
    if (match) {
      return `${API}/public/files/${match[1]}/${match[2]}` || `${API}/files/${match[2]}`;
    }
    const remainder = url.split('integrations.emergentagent.com/objstore/api/v1/storage/objects/')[1];
    if (remainder) {
      return `${API}/public/files/${remainder}`;
    }
  }
  return url;
};

const CONTENT_TYPES = [
  { value: 'video', label: 'Video Upload', icon: Video, color: 'text-blue-400' },
  { value: 'embed', label: 'Video Embed (YouTube/Vimeo)', icon: Youtube, color: 'text-red-400' },
  { value: 'pdf', label: 'PDF Document', icon: FileText, color: 'text-orange-400' },
  { value: 'scorm', label: 'SCORM Package', icon: Package, color: 'text-purple-400' },
  { value: 'moodle', label: 'Moodle Course URL', icon: GraduationCap, color: 'text-cyan-400' },
  { value: 'json', label: 'JSON Learning Object', icon: Code, color: 'text-green-400' },
  { value: 'quiz', label: 'Quiz Assessment', icon: ClipboardList, color: 'text-navigate-gold' },
];

export default function ModuleBuilder({ courseId, token, onClose }) {
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});
  const [publishing, setPublishing] = useState(false);
  
  // Modal states
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [activeModuleId, setActiveModuleId] = useState(null);
  const [showQuizBuilder, setShowQuizBuilder] = useState(false);
  const [editingQuizId, setEditingQuizId] = useState(null);

  useEffect(() => {
    fetchCourseAndModules();
  }, [courseId]);

  const fetchCourseAndModules = async () => {
    try {
      const [courseRes, modulesRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`),
        axios.get(`${API}/courses/${courseId}/modules`)
      ]);
      setCourse(courseRes.data);
      setModules(modulesRes.data.modules || []);
      
      // Expand all modules by default
      const expanded = {};
      (modulesRes.data.modules || []).forEach(m => {
        expanded[m.module_id] = true;
      });
      setExpandedModules(expanded);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module and all its content?')) return;
    
    try {
      await axios.delete(`${API}/courses/${courseId}/modules/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Module deleted');
      fetchCourseAndModules();
    } catch (error) {
      toast.error('Failed to delete module');
    }
  };

  const handleDeleteContent = async (moduleId, contentId) => {
    if (!window.confirm('Delete this content?')) return;
    
    try {
      await axios.delete(`${API}/courses/${courseId}/modules/${moduleId}/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content deleted');
      fetchCourseAndModules();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const handleViewContent = (content) => {
    // For different content types, open appropriately
    if (content.content_type === 'embed') {
      window.open(content.embed_url, '_blank');
      return;
    }
    
    // SCORM content - check if it has been properly processed
    if (content.content_type === 'scorm') {
      if (content.scorm_package_id) {
        // Properly processed SCORM - open in SCORM Player
        window.open(`/scorm/${content.scorm_package_id}`, '_blank');
      } else {
        // Raw SCORM zip - show message that it needs processing
        toast.info('This SCORM package needs to be processed. Please re-upload using the SCORM upload option or contact admin.', {
          duration: 5000
        });
      }
      return;
    }
    
    if (content.content_type === 'quiz' && content.quiz_id) {
      window.open(`/quiz/${content.quiz_id}`, '_blank');
      return;
    }
    
    if (content.file_url) {
      // Use proxy URL with token for authenticated access in new tab
      const viewUrl = getProxyUrl(content.file_url);
      // Append token as query parameter for new tab authentication
      const urlWithToken = `${viewUrl}?token=${encodeURIComponent(token)}`;
      window.open(urlWithToken, '_blank');
    }
  };

  const handlePublishCourse = async () => {
    if (modules.length === 0) {
      toast.error('Please add at least one module before publishing');
      return;
    }
    
    const totalContent = modules.reduce((sum, m) => sum + (m.contents?.length || 0), 0);
    if (totalContent === 0) {
      toast.error('Please add content to your modules before publishing');
      return;
    }
    
    if (!window.confirm(`Publish "${course?.title}"? This will make the course visible to learners.`)) {
      return;
    }
    
    setPublishing(true);
    try {
      await axios.put(`${API}/admin/courses/${courseId}`, 
        { status: 'published' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Course published successfully!');
      fetchCourseAndModules();
    } catch (error) {
      console.error('Error publishing course:', error);
      toast.error('Failed to publish course');
    } finally {
      setPublishing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const getContentIcon = (type) => {
    const contentType = CONTENT_TYPES.find(ct => ct.value === type);
    if (contentType) {
      const Icon = contentType.icon;
      return <Icon className={`h-5 w-5 ${contentType.color}`} />;
    }
    return <File className="h-5 w-5 text-zinc-400" />;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex bg-navigate-dark overflow-hidden">
      {/* Sidebar */}
      <div className="w-80 bg-navigate-surface border-r border-zinc-800 flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4"
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
            Back to Courses
          </button>
          <h2 className="font-heading text-xl font-bold text-white truncate">
            {course?.title}
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            {modules.length} module{modules.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Module List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {modules.sort((a, b) => a.order - b.order).map((module, idx) => (
              <button
                key={module.module_id}
                onClick={() => toggleModule(module.module_id)}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                  expandedModules[module.module_id]
                    ? 'bg-navigate-gold/10 text-navigate-gold border border-navigate-gold/30'
                    : 'bg-zinc-800/50 text-zinc-300 hover:bg-zinc-800'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-navigate-gold/20 text-navigate-gold flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="flex-1 truncate text-sm font-medium">{module.title}</span>
                  <span className="text-xs text-zinc-500">
                    {module.contents?.length || 0}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <button
            onClick={() => {
              setEditingModule(null);
              setShowModuleModal(true);
            }}
            className="w-full mt-4 px-4 py-3 border border-dashed border-zinc-700 rounded-lg text-zinc-400 hover:border-navigate-gold hover:text-navigate-gold transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Module
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header with Publish Button */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-white">
                Module Builder
              </h1>
              <p className="text-sm text-zinc-400 mt-1">
                Build and organize your course content
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Course Status Badge */}
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                course?.status === 'published' 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
              }`}>
                {course?.status === 'published' ? 'Published' : 'Draft'}
              </span>
              
              {/* Publish Button - show for draft or no status */}
              {course?.status !== 'published' && (
                <button
                  onClick={handlePublishCourse}
                  disabled={publishing}
                  className="btn-gold flex items-center gap-2 disabled:opacity-50"
                  data-testid="publish-course-btn"
                >
                  <Send className="h-4 w-4" />
                  {publishing ? 'Publishing...' : 'Publish Course'}
                </button>
              )}
            </div>
          </div>

          {modules.length === 0 ? (
            <div className="text-center py-16 bg-navigate-surface border border-zinc-800 rounded-xl">
              <Package className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">No modules yet</h3>
              <p className="text-zinc-400 mb-6">
                Start building your course by adding modules
              </p>
              <button
                onClick={() => {
                  setEditingModule(null);
                  setShowModuleModal(true);
                }}
                className="btn-gold inline-flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Add First Module
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {modules.sort((a, b) => a.order - b.order).map((module, idx) => (
                <div
                  key={module.module_id}
                  className={`bg-navigate-surface border rounded-xl overflow-hidden transition-all ${
                    expandedModules[module.module_id] 
                      ? 'border-navigate-gold/30' 
                      : 'border-zinc-800'
                  }`}
                >
                  {/* Module Header */}
                  <div 
                    className="px-6 py-4 flex items-center gap-4 cursor-pointer hover:bg-zinc-800/30"
                    onClick={() => toggleModule(module.module_id)}
                  >
                    <div className="flex items-center gap-3">
                      <GripVertical className="h-5 w-5 text-zinc-600" />
                      <span className="w-8 h-8 rounded-full bg-navigate-gold text-black flex items-center justify-center font-bold">
                        {idx + 1}
                      </span>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-white">{module.title}</h3>
                      <p className="text-sm text-zinc-400">{module.description}</p>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
                        {module.contents?.length || 0} items
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingModule(module);
                          setShowModuleModal(true);
                        }}
                        className="p-2 text-zinc-400 hover:text-white"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteModule(module.module_id);
                        }}
                        className="p-2 text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      {expandedModules[module.module_id] ? (
                        <ChevronDown className="h-5 w-5 text-zinc-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-zinc-400" />
                      )}
                    </div>
                  </div>

                  {/* Module Contents */}
                  {expandedModules[module.module_id] && (
                    <div className="border-t border-zinc-800 p-4">
                      {/* Content List */}
                      <div className="space-y-2 mb-4">
                        {(module.contents || []).sort((a, b) => a.order - b.order).map((content, cIdx) => (
                          <div
                            key={content.content_id}
                            className="flex items-center gap-4 p-4 bg-zinc-800/50 rounded-lg group"
                          >
                            <span className="text-xs text-zinc-500 w-6">{cIdx + 1}</span>
                            {getContentIcon(content.content_type)}
                            
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">
                                {content.title}
                              </p>
                              <div className="flex items-center gap-3 text-xs text-zinc-500">
                                <span className="capitalize">{content.content_type}</span>
                                {content.duration_minutes > 0 && (
                                  <span>{content.duration_minutes} min</span>
                                )}
                                {content.file_size > 0 && (
                                  <span>{formatFileSize(content.file_size)}</span>
                                )}
                                {content.embed_url && (
                                  <span className="flex items-center gap-1">
                                    <LinkIcon className="h-3 w-3" />
                                    Embedded
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1">
                              {/* View Button - Always visible */}
                              <button
                                onClick={() => handleViewContent(content)}
                                className="p-2 text-zinc-400 hover:text-navigate-gold transition-colors"
                                title="View/Preview"
                                data-testid={`view-content-${content.content_id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {/* Delete Button */}
                              <button
                                onClick={() => handleDeleteContent(module.module_id, content.content_id)}
                                className="p-2 text-zinc-400 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                                title="Delete"
                                data-testid={`delete-content-${content.content_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Add Content Button */}
                      <button
                        onClick={() => {
                          setActiveModuleId(module.module_id);
                          setShowContentModal(true);
                        }}
                        className="w-full py-3 border border-dashed border-zinc-700 rounded-lg text-zinc-400 hover:border-navigate-gold hover:text-navigate-gold transition-colors flex items-center justify-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add Content
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Module Modal */}
      {showModuleModal && (
        <ModuleModal
          module={editingModule}
          courseId={courseId}
          token={token}
          nextOrder={modules.length + 1}
          onClose={() => {
            setShowModuleModal(false);
            setEditingModule(null);
          }}
          onSave={() => {
            setShowModuleModal(false);
            setEditingModule(null);
            fetchCourseAndModules();
          }}
        />
      )}

      {/* Content Modal */}
      {showContentModal && (
        <ContentModal
          courseId={courseId}
          moduleId={activeModuleId}
          token={token}
          nextOrder={(modules.find(m => m.module_id === activeModuleId)?.contents?.length || 0) + 1}
          onClose={() => {
            setShowContentModal(false);
            setActiveModuleId(null);
          }}
          onSave={() => {
            setShowContentModal(false);
            setActiveModuleId(null);
            fetchCourseAndModules();
          }}
          onOpenQuizBuilder={() => {
            setShowContentModal(false);
            setShowQuizBuilder(true);
          }}
        />
      )}

      {/* Quiz Builder Modal */}
      {showQuizBuilder && (
        <QuizBuilder
          courseId={courseId}
          moduleId={activeModuleId}
          quizId={editingQuizId}
          token={token}
          onClose={() => {
            setShowQuizBuilder(false);
            setActiveModuleId(null);
            setEditingQuizId(null);
          }}
          onSave={() => {
            setShowQuizBuilder(false);
            setActiveModuleId(null);
            setEditingQuizId(null);
            fetchCourseAndModules();
          }}
        />
      )}
    </div>
  );
}

// Module Create/Edit Modal
function ModuleModal({ module, courseId, token, nextOrder, onClose, onSave }) {
  const isEditing = !!module;
  const [formData, setFormData] = useState({
    title: module?.title || '',
    description: module?.description || '',
    order: module?.order || nextOrder
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        await axios.put(
          `${API}/courses/${courseId}/modules/${module.module_id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/courses/${courseId}/modules`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success(isEditing ? 'Module updated' : 'Module created');
      onSave();
    } catch (error) {
      toast.error('Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl w-full max-w-lg">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-white">
            {isEditing ? 'Edit Module' : 'Add New Module'}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Module Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., Introduction to Compliance"
              className="bg-navigate-dark border-zinc-700"
              required
            />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Brief description of what this module covers..."
              className="bg-navigate-dark border-zinc-700"
              rows={3}
            />
          </div>

          <div>
            <Label>Order</Label>
            <Input
              type="number"
              min="1"
              value={formData.order}
              onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 1})}
              className="bg-navigate-dark border-zinc-700 w-24"
            />
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2 text-zinc-400 hover:text-white">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-gold disabled:opacity-50">
              {saving ? 'Saving...' : (isEditing ? 'Update Module' : 'Create Module')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Content Upload Modal
function ContentModal({ courseId, moduleId, token, nextOrder, onClose, onSave, onOpenQuizBuilder }) {
  const [contentType, setContentType] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    embed_url: '',
    moodle_url: '',
    duration_minutes: 0,
    order: nextOrder
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Auto-fill title from filename if empty
      if (!formData.title) {
        const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '');
        setFormData(prev => ({ ...prev, title: nameWithoutExt }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contentType) {
      toast.error('Please select a content type');
      return;
    }

    // For quiz type, open quiz builder
    if (contentType === 'quiz') {
      onOpenQuizBuilder();
      return;
    }

    if (contentType === 'embed') {
      if (!formData.embed_url) {
        toast.error('Please enter a video URL');
        return;
      }
      await addEmbed();
    } else if (contentType === 'moodle') {
      if (!formData.moodle_url) {
        toast.error('Please enter a Moodle course URL');
        return;
      }
      await addMoodleContent();
    } else {
      if (!file) {
        toast.error('Please select a file to upload');
        return;
      }
      await uploadFile();
    }
  };

  const addMoodleContent = async () => {
    setUploading(true);
    try {
      const params = new URLSearchParams({
        title: formData.title || 'Moodle Course',
        moodle_url: formData.moodle_url,
        description: formData.description,
        order: formData.order.toString()
      });

      await axios.post(
        `${API}/courses/${courseId}/modules/${moduleId}/moodle?${params}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Moodle course link added');
      onSave();
      onClose();
    } catch (error) {
      console.error('Error adding Moodle content:', error);
      toast.error(error.response?.data?.detail || 'Failed to add Moodle course');
    } finally {
      setUploading(false);
    }
  };

  const addEmbed = async () => {
    setUploading(true);
    try {
      const params = new URLSearchParams({
        title: formData.title,
        embed_url: formData.embed_url,
        description: formData.description,
        duration_minutes: formData.duration_minutes.toString(),
        order: formData.order.toString()
      });

      await axios.post(
        `${API}/courses/${courseId}/modules/${moduleId}/embed?${params}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Video embed added');
      onSave();
    } catch (error) {
      toast.error('Failed to add embed');
    } finally {
      setUploading(false);
    }
  };

  const uploadFile = async () => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const params = new URLSearchParams({
        title: formData.title,
        description: formData.description,
        duration_minutes: formData.duration_minutes.toString(),
        order: formData.order.toString()
      });

      // Determine endpoint based on content type
      let endpoint = '';
      switch (contentType) {
        case 'video':
          endpoint = `${API}/courses/${courseId}/modules/${moduleId}/upload/video?${params}`;
          break;
        case 'pdf':
          endpoint = `${API}/courses/${courseId}/modules/${moduleId}/upload/pdf?${params}`;
          break;
        case 'scorm':
          // Use the SCORM package upload endpoint which properly extracts and parses
          const scormParams = new URLSearchParams({
            course_id: courseId,
            module_id: moduleId,
            title: formData.title,
            description: formData.description
          });
          endpoint = `${API}/scorm/packages/upload?${scormParams}`;
          break;
        case 'json':
          endpoint = `${API}/courses/${courseId}/modules/${moduleId}/upload/json?${params}`;
          break;
        default:
          throw new Error('Invalid content type');
      }

      await axios.post(endpoint, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success('File uploaded successfully');
      onSave();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const getAcceptedFiles = () => {
    switch (contentType) {
      case 'video':
        return 'video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg,.mp4,.webm,.mov,.avi';
      case 'pdf':
        return 'application/pdf,.pdf';
      case 'scorm':
        return 'application/zip,.zip';
      case 'json':
        return 'application/json,.json';
      default:
        return '*';
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-navigate-surface">
          <h2 className="font-heading text-xl font-bold text-white">Add Content</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Content Type Selection */}
          <div>
            <Label>Content Type</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
              {CONTENT_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => {
                    setContentType(type.value);
                    setFile(null);
                  }}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    contentType === type.value
                      ? 'border-navigate-gold bg-navigate-gold/10'
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <type.icon className={`h-6 w-6 mb-2 ${type.color}`} />
                  <p className="text-sm font-medium text-white">{type.label}</p>
                </button>
              ))}
            </div>
          </div>

          {contentType && (
            <>
              {/* Quiz Builder Prompt */}
              {contentType === 'quiz' && (
                <div className="text-center py-6">
                  <ClipboardList className="h-16 w-16 text-navigate-gold mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Create Quiz Assessment</h3>
                  <p className="text-zinc-400 mb-6 text-sm">
                    Build an interactive quiz with multiple choice, true/false, and short answer questions.
                  </p>
                  <button
                    type="submit"
                    className="btn-gold inline-flex items-center gap-2"
                  >
                    <Plus className="h-5 w-5" />
                    Open Quiz Builder
                  </button>
                </div>
              )}

              {/* Title - Not for quiz */}
              {contentType !== 'quiz' && (
                <div>
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Content title"
                    className="bg-navigate-dark border-zinc-700"
                    required
                  />
                </div>
              )}

              {/* Description - Not for quiz */}
              {contentType !== 'quiz' && (
                <div>
                  <Label>Description (optional)</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Brief description..."
                    className="bg-navigate-dark border-zinc-700"
                    rows={2}
                  />
                </div>
              )}

              {/* Video Embed URL */}
              {contentType === 'embed' && (
                <div>
                  <Label>Video URL</Label>
                  <Input
                    value={formData.embed_url}
                    onChange={(e) => setFormData({...formData, embed_url: e.target.value})}
                    placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                    className="bg-navigate-dark border-zinc-700"
                    required
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Supports YouTube, Vimeo, and other embed URLs
                  </p>
                </div>
              )}

              {/* Moodle Course URL */}
              {contentType === 'moodle' && (
                <div>
                  <Label>Moodle Course URL</Label>
                  <Input
                    value={formData.moodle_url}
                    onChange={(e) => setFormData({...formData, moodle_url: e.target.value})}
                    placeholder="https://moodle.yourcompany.com/course/view.php?id=123"
                    className="bg-navigate-dark border-zinc-700"
                    required
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Enter the full URL to the Moodle course. Learners will be redirected to this URL when they click "Launch Course".
                  </p>
                  <div className="mt-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                    <p className="text-sm text-cyan-300 flex items-center gap-2">
                      <GraduationCap className="h-4 w-4" />
                      Moodle Integration
                    </p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Users will be launched directly to your Moodle course. Ensure your Moodle site allows external access or has appropriate SSO configured.
                    </p>
                  </div>
                </div>
              )}

              {/* File Upload */}
              {contentType !== 'embed' && contentType !== 'quiz' && contentType !== 'moodle' && (
                <div>
                  <Label>
                    {contentType === 'video' && 'Video File (MP4, WebM, MOV, AVI - max 500MB)'}
                    {contentType === 'pdf' && 'PDF Document'}
                    {contentType === 'scorm' && 'SCORM Package (ZIP file - max 500MB)'}
                    {contentType === 'json' && 'JSON Learning Object'}
                  </Label>
                  <div className="mt-2">
                    <label className="block">
                      <div className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        file 
                          ? 'border-navigate-gold bg-navigate-gold/5' 
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}>
                        {file ? (
                          <div className="flex items-center justify-center gap-3">
                            <FileText className="h-8 w-8 text-navigate-gold" />
                            <div className="text-left">
                              <p className="text-sm font-medium text-white">{file.name}</p>
                              <p className="text-xs text-zinc-400">
                                {(file.size / (1024 * 1024)).toFixed(2)} MB
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setFile(null);
                              }}
                              className="ml-4 p-1 text-zinc-400 hover:text-red-400"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <Upload className="h-10 w-10 text-zinc-500 mx-auto mb-3" />
                            <p className="text-sm text-zinc-400">
                              Click to select or drag and drop
                            </p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        accept={getAcceptedFiles()}
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Duration (for videos) */}
              {(contentType === 'video' || contentType === 'embed') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 0})}
                      className="bg-navigate-dark border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label>Order</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.order}
                      onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 1})}
                      className="bg-navigate-dark border-zinc-700"
                    />
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Uploading...</span>
                    <span className="text-navigate-gold">{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}
            </>
          )}

          {/* Footer buttons - hide for quiz since it has its own button */}
          {contentType !== 'quiz' && (
            <div className="flex justify-end gap-4 pt-4 border-t border-zinc-800">
              <button 
                type="button" 
                onClick={onClose} 
                disabled={uploading}
                className="px-6 py-2 text-zinc-400 hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={uploading || !contentType}
                className="btn-gold disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : (contentType === 'embed' ? 'Add Embed' : 'Upload')}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
